<template>
  <div class="ocr-page-section ocr-config-page">
    <OcrPageShimmer v-if="initialLoading" variant="configuration" />
    <OcrApiErrorPanel
      v-else-if="loadError"
      :message="loadError"
      :retrying="initialLoading"
      @retry="initPage"
    />
    <template v-else>
    <div class="ocr-panel ocr-panel--intro">
      <p class="ocr-panel-lead ocr-panel-lead--flush">
        Edit digitization mapping CSVs used for table, column, UOM, component, and enumeration mapping.
      </p>
    </div>
    <div class="ocr-config-layout">
      <v-list density="compact" class="ocr-config-nav">
        <v-list-item
          v-for="section in sections"
          :key="section.id"
          :active="section.id === activeSection"
          @click="selectSection(section.id)"
        >
          <v-list-item-title>{{ formatSection(section.id) }}</v-list-item-title>
          <v-list-item-subtitle>{{ section.row_count }} rows</v-list-item-subtitle>
        </v-list-item>
      </v-list>

      <div ref="configEditor" class="ocr-config-editor">
        <OcrTableSectionSkeleton
          v-if="loading"
          embedded
          :columns="['Field', 'Value', 'Notes']"
          :row-count="8"
        />
        <template v-else>
          <div class="ocr-config-sticky">
            <div class="ocr-config-editor-head">
              <div class="ocr-config-title">
                <div class="ocr-panel-title ocr-panel-title--flush">{{ formatSection(activeSection) }}</div>
                <p class="ocr-panel-lead ocr-panel-lead--flush">{{ rowSummary }}</p>
              </div>
              <div class="ocr-config-filter">
                <div class="ocr-field">
                  <label class="profile-menu-label">Filter column</label>
                  <v-select
                    v-model="filterColumn"
                    :items="columnItems"
                    density="comfortable"
                    variant="outlined"
                    hide-details
                    clearable
                    menu-icon="mdi-chevron-down"
                  />
                </div>
                <div class="ocr-field">
                  <label class="profile-menu-label">Values</label>
                  <v-select
                    v-model="filterValues"
                    :items="filterValueOptions"
                    density="comfortable"
                    variant="outlined"
                    hide-details
                    multiple
                    chips
                    closable-chips
                    menu-icon="mdi-chevron-down"
                    :disabled="!filterColumn"
                  />
                </div>
                <div class="ocr-field">
                  <label class="profile-menu-label">Sort by</label>
                  <v-select
                    v-model="sortColumns"
                    :items="columnItems"
                    density="comfortable"
                    variant="outlined"
                    hide-details
                    multiple
                    chips
                    closable-chips
                    menu-icon="mdi-chevron-down"
                  />
                </div>
              </div>
              <div class="ocr-config-actions">
                <v-btn size="small" variant="outlined" class="ocr-toolbar-btn" :disabled="!canUndo" @click="undo">Undo</v-btn>
                <v-btn size="small" variant="outlined" class="ocr-toolbar-btn" :disabled="!canRedo" @click="redo">Redo</v-btn>
                <v-btn size="small" variant="outlined" class="ocr-toolbar-btn" @click="reloadSection">Reload</v-btn>
                <v-btn size="small" variant="tonal" class="ocr-toolbar-btn" :disabled="!columns.length" @click="addRow">Add row</v-btn>
                <v-btn size="small" color="primary" variant="flat" class="ocr-toolbar-btn" :loading="saving" @click="saveSection">
                  Save
                </v-btn>
              </div>
            </div>
            <v-alert
              v-if="statusMessage"
              ref="statusAlert"
              :type="statusType"
              variant="tonal"
              density="compact"
              class="ocr-status-alert ocr-config-status"
              closable
              @click:close="statusMessage = ''"
            >
              {{ statusMessage }}
            </v-alert>
          </div>
          <p v-if="!columns.length" class="ocr-empty-cell">No table data to edit.</p>
          <template v-else>
            <OcrTableWrap>
              <v-table class="well-table sample-table well-table-data fd-table">
                <thead>
                  <tr class="main-header">
                    <th v-for="col in columns" :key="col">{{ col }}</th>
                    <th class="ocr-config-row-action"></th>
                  </tr>
                </thead>
                <tbody class="well-table-content">
                  <tr v-for="idx in pagedIndexes" :key="idx">
                    <td v-for="col in columns" :key="col + idx">
                      <input
                        v-model="records[idx][col]"
                        class="ocr-editable-grid-input"
                        type="text"
                        @focus="onCellFocus"
                        @input="onCellInput"
                        @blur="onCellBlur"
                      />
                    </td>
                    <td class="ocr-config-row-action">
                      <v-btn
                        icon="mdi-close"
                        size="x-small"
                        variant="text"
                        class="ocr-config-remove-row"
                        aria-label="Remove row"
                        @click="removeRow(idx)"
                      />
                    </td>
                  </tr>
                  <tr v-if="!pagedIndexes.length">
                    <td :colspan="columns.length + 1" class="ocr-empty-cell">No rows match the current filter.</td>
                  </tr>
                </tbody>
              </v-table>
            </OcrTableWrap>
            <div v-if="filteredCount > pageSize" class="ocr-config-pager">
              <v-btn size="small" variant="outlined" class="ocr-toolbar-btn" :disabled="page <= 1" @click="page -= 1">
                Previous
              </v-btn>
              <span class="ocr-config-pager-info">
                {{ pageStart }}–{{ pageEnd }} of {{ filteredCount }}
              </span>
              <v-btn
                size="small"
                variant="outlined"
                class="ocr-toolbar-btn"
                :disabled="page >= pageCount"
                @click="page += 1"
              >
                Next
              </v-btn>
              <v-select
                v-model="pageSize"
                :items="pageSizeItems"
                density="compact"
                variant="outlined"
                hide-details
                class="ocr-config-page-size"
              />
            </div>
          </template>
          <div v-if="excelSheet" class="ocr-config-excel">
            <div class="ocr-config-excel-copy">
              <span class="ocr-config-excel-label">Master Excel</span>
              <span class="ocr-config-excel-name">{{ excelFile ? excelFile.name : `Sheet: ${excelSheet}` }}</span>
            </div>
            <div class="ocr-toolbar-cluster">
              <input ref="excelInput" type="file" accept=".xlsx" hidden @change="onExcelSelected" />
              <v-btn size="small" variant="outlined" class="ocr-toolbar-btn" @click="$refs.excelInput.click()">Choose .xlsx</v-btn>
              <v-btn size="small" variant="tonal" class="ocr-toolbar-btn" :disabled="!excelFile" :loading="importing" @click="confirmImport">
                Import
              </v-btn>
              <v-btn size="small" color="primary" variant="flat" class="ocr-toolbar-btn" :disabled="!excelFile" :loading="exporting" @click="confirmExport">
                Export
              </v-btn>
            </div>
          </div>
        </template>
      </div>
    </div>
    </template>

    <v-dialog v-model="confirmOpen" max-width="460">
      <v-card class="ocr-dialog-card">
        <v-card-title class="ocr-dialog-title">{{ confirmTitle }}</v-card-title>
        <v-card-text>{{ confirmMessage }}</v-card-text>
        <v-card-actions class="ocr-dialog-actions">
          <v-spacer />
          <v-btn variant="text" class="ocr-toolbar-btn" @click="confirmOpen = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" class="ocr-toolbar-btn" @click="runConfirm">Okay</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";
import OcrPageShimmer from "@/components/layout/OCR/OcrPageShimmer.vue";
import OcrApiErrorPanel from "@/components/layout/OCR/OcrApiErrorPanel.vue";
import OcrTableSectionSkeleton from "@/components/layout/OCR/OcrTableSectionSkeleton.vue";
import OcrTableWrap from "@/components/layout/OCR/OcrTableWrap.vue";

export default {
  name: "OcrConfiguration",
  components: { OcrPageShimmer, OcrApiErrorPanel, OcrTableSectionSkeleton, OcrTableWrap },
  data() {
    return {
      sections: [],
      activeSection: "tables",
      records: [],
      columns: [],
      excelSheet: null,
      excelFile: null,
      filterColumn: null,
      filterValues: [],
      sortColumns: [],
      initialLoading: true,
      loadError: null,
      loading: false,
      saving: false,
      importing: false,
      exporting: false,
      statusMessage: "",
      statusType: "info",
      confirmOpen: false,
      confirmTitle: "",
      confirmMessage: "",
      confirmAction: null,
      sectionCache: {},
      page: 1,
      pageSize: 50,
      pageSizeItems: [25, 50, 100, 200],
      undoStack: [],
      redoStack: [],
      pendingHistory: null,
    };
  },
  computed: {
    columnItems() {
      return this.columns;
    },
    filterValueOptions() {
      if (!this.filterColumn) return [];
      const vals = new Set();
      this.records.forEach((row) => {
        const value = row[this.filterColumn];
        if (value != null && String(value) !== "") vals.add(String(value));
      });
      return [...vals].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
    },
    filteredIndexes() {
      let idxs = this.records.map((_, i) => i);
      if (this.filterColumn && this.filterValues.length) {
        idxs = idxs.filter((i) => this.filterValues.includes(String(this.records[i][this.filterColumn] ?? "")));
      }
      if (this.sortColumns.length) {
        idxs = [...idxs].sort((a, b) => {
          for (const col of this.sortColumns) {
            const cmp = String(this.records[a][col] ?? "").localeCompare(String(this.records[b][col] ?? ""), undefined, {
              numeric: true,
              sensitivity: "base",
            });
            if (cmp) return cmp;
          }
          return 0;
        });
      }
      return idxs;
    },
    filteredCount() {
      return this.filteredIndexes.length;
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.filteredCount / this.pageSize) || 1);
    },
    pagedIndexes() {
      const start = (this.page - 1) * this.pageSize;
      return this.filteredIndexes.slice(start, start + this.pageSize);
    },
    pageStart() {
      if (!this.filteredCount) return 0;
      return (this.page - 1) * this.pageSize + 1;
    },
    pageEnd() {
      return Math.min(this.filteredCount, this.page * this.pageSize);
    },
    rowSummary() {
      const shown = this.filteredCount;
      const total = this.records.length;
      if (shown === total) return `${total} rows`;
      return `${shown} of ${total} rows`;
    },
    canUndo() {
      return this.undoStack.length > 0;
    },
    canRedo() {
      return this.redoStack.length > 0;
    },
  },
  watch: {
    filterColumn() {
      this.filterValues = [];
      this.page = 1;
    },
    filterValues() {
      this.page = 1;
    },
    sortColumns() {
      this.page = 1;
    },
    pageSize() {
      this.page = 1;
    },
  },
  mounted() {
    this.initPage();
  },
  methods: {
    async initPage() {
      this.initialLoading = true;
      this.loadError = null;
      try {
        await this.loadSections();
        await this.selectSection(this.activeSection, { reload: true });
        this.prefetchRest();
      } catch (e) {
        this.loadError = formatOcrApiError(e, "Could not load configuration.");
      } finally {
        this.initialLoading = false;
      }
    },
    formatSection(id) {
      return id.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    applyPayload(data) {
      this.records = data.records || [];
      this.columns = data.columns?.length
        ? data.columns
        : this.records.length
          ? Object.keys(this.records[0])
          : [];
      this.excelSheet = data.excel_sheet || null;
    },
    cacheSection(id, data) {
      const records = (data.records || []).map((row) => ({ ...row }));
      const entry = {
        records,
        columns: data.columns?.length ? data.columns : (records[0] ? Object.keys(records[0]) : []),
        excel_sheet: data.excel_sheet || null,
      };
      this.sectionCache[id] = entry;
      return entry;
    },
    async prefetchRest() {
      for (const section of this.sections) {
        if (this.sectionCache[section.id]) continue;
        try {
          const { data } = await ocrApi.get(`api/v1/config/sections/${section.id}`);
          if (!this.sectionCache[section.id]) this.cacheSection(section.id, data);
        } catch {
          /* leave uncached; click will fetch */
        }
      }
    },
    resetFilters() {
      this.filterColumn = null;
      this.filterValues = [];
      this.sortColumns = [];
    },
    async loadSections() {
      const { data } = await ocrApi.get("api/v1/config/sections");
      this.sections = data.sections || [];
    },
    async selectSection(id, { reload = false } = {}) {
      if (id === this.activeSection && this.sectionCache[id] && !reload) return;
      this.activeSection = id;
      this.statusMessage = "";
      this.excelFile = null;
      this.resetFilters();
      this.resetHistory();
      this.page = 1;
      const cached = !reload ? this.sectionCache[id] : null;
      if (cached) {
        this.applyPayload(cached);
        this.loading = false;
        return;
      }
      this.loading = true;
      try {
        const { data } = await ocrApi.get(`api/v1/config/sections/${id}`);
        this.applyPayload(this.cacheSection(id, data));
      } finally {
        this.loading = false;
      }
    },
    async reloadSection() {
      delete this.sectionCache[this.activeSection];
      await this.selectSection(this.activeSection, { reload: true });
    },
    addRow() {
      this.pushHistory();
      const blank = {};
      this.columns.forEach((col) => {
        blank[col] = "";
      });
      this.records.push(blank);
      this.syncCachedRecords();
      this.page = this.pageCount;
    },
    removeRow(idx) {
      this.pushHistory();
      this.records.splice(idx, 1);
      this.syncCachedRecords();
      this.clampPage();
    },
    undo() {
      if (!this.undoStack.length) return;
      this.redoStack.push(this.cloneRecords());
      this.records = this.undoStack.pop();
      this.pendingHistory = null;
      this.syncCachedRecords();
      this.clampPage();
    },
    redo() {
      if (!this.redoStack.length) return;
      this.undoStack.push(this.cloneRecords());
      this.records = this.redoStack.pop();
      this.pendingHistory = null;
      this.syncCachedRecords();
      this.clampPage();
    },
    cloneRecords(rows = this.records) {
      return rows.map((row) => ({ ...row }));
    },
    pushHistory() {
      this.undoStack.push(this.cloneRecords());
      if (this.undoStack.length > 50) this.undoStack.shift();
      this.redoStack = [];
      this.pendingHistory = null;
    },
    resetHistory() {
      this.undoStack = [];
      this.redoStack = [];
      this.pendingHistory = null;
    },
    syncCachedRecords() {
      const cached = this.sectionCache[this.activeSection];
      if (cached) cached.records = this.records;
    },
    clampPage() {
      if (this.page > this.pageCount) this.page = this.pageCount;
    },
    onCellFocus() {
      this.pendingHistory = this.cloneRecords();
    },
    onCellInput() {
      if (!this.pendingHistory) return;
      this.undoStack.push(this.pendingHistory);
      if (this.undoStack.length > 50) this.undoStack.shift();
      this.redoStack = [];
      this.pendingHistory = null;
    },
    onCellBlur() {
      this.pendingHistory = null;
    },
    revealStatus() {
      this.$nextTick(() => {
        const pane = this.$refs.configEditor;
        if (pane && typeof pane.scrollTo === "function") pane.scrollTo({ top: 0, behavior: "smooth" });
      });
    },
    async saveSection() {
      this.saving = true;
      this.statusMessage = "";
      try {
        await ocrApi.put(`api/v1/config/sections/${this.activeSection}`, {
          records: this.records,
        });
        await this.loadSections();
        this.statusMessage = "Configuration saved.";
        this.statusType = "success";
        this.revealStatus();
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Save failed.");
        this.statusType = "error";
        this.revealStatus();
      } finally {
        this.saving = false;
      }
    },
    onExcelSelected(event) {
      this.excelFile = event.target.files?.[0] || null;
      event.target.value = "";
    },
    confirmImport() {
      this.confirmTitle = "Import from master";
      this.confirmMessage = `Import sheet ${this.excelSheet} and overwrite the current ${this.formatSection(this.activeSection)} configuration?`;
      this.confirmAction = "import";
      this.confirmOpen = true;
    },
    confirmExport() {
      this.confirmTitle = "Export to master";
      this.confirmMessage = `Export the current ${this.formatSection(this.activeSection)} configuration onto sheet ${this.excelSheet} in ${this.excelFile?.name}?`;
      this.confirmAction = "export";
      this.confirmOpen = true;
    },
    async runConfirm() {
      this.confirmOpen = false;
      if (this.confirmAction === "import") await this.importExcel();
      if (this.confirmAction === "export") await this.exportExcel();
    },
    excelForm() {
      const form = new FormData();
      form.append("file", this.excelFile);
      return form;
    },
    async importExcel() {
      if (!this.excelFile) return;
      this.importing = true;
      this.statusMessage = "";
      try {
        const { data } = await ocrApi.post(
          `api/v1/config/sections/${this.activeSection}/import-excel`,
          this.excelForm(),
        );
        this.applyPayload(this.cacheSection(this.activeSection, data));
        await this.loadSections();
        this.statusMessage = `Imported ${this.excelSheet}.`;
        this.statusType = "success";
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Excel import failed.");
        this.statusType = "error";
      } finally {
        this.importing = false;
      }
    },
    async exportExcel() {
      if (!this.excelFile) return;
      this.exporting = true;
      this.statusMessage = "";
      try {
        const res = await ocrApi.post(
          `api/v1/config/sections/${this.activeSection}/export-excel`,
          this.excelForm(),
          { responseType: "blob" },
        );
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = this.excelFile.name || "master.xlsx";
        a.click();
        URL.revokeObjectURL(url);
        this.statusMessage = `Exported ${this.excelSheet}. Download the updated master workbook.`;
        this.statusType = "success";
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Excel export failed.");
        this.statusType = "error";
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>
